/**
 * A lista de isentos da concessionária (`A-06`, ADR-065).
 *
 * **Operar a lista não exige deploy** — é o aceite da história. A supervisão inclui uma placa
 * por vez, com a tipologia, a concessão (ou todas), a vigência e o **fundamento**, ou importa
 * a planilha da frota inteira, com prévia antes de gravar. Remover é encerrar a vigência agora:
 * a passagem de ontem continua isenta se a placa estava na lista ontem, e a linha removida fica
 * no histórico, com quem, quando e por quê.
 *
 * **A tela nunca apresenta a lista como base oficial**: a fonte está no topo e em toda isenção
 * que ela produz, na conferência e no detalhe.
 *
 * **A ordem das fontes** é do servidor (`transit_domain.isentos`): a lista, o cadastro do
 * veículo e, quando a placa não está em nenhum dos dois, a caracterização — a viatura, o carro
 * de prefeitura, o operacional da concessionária, pelo que está escrito nele. O quadro de
 * veículos operacionais mostra o que a IA procura.
 */
import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router";

import { ErroDaApi, api } from "../api/cliente";
import { useSessao } from "../api/contexto-sessao";
import type {
  LinhaDaLista,
  ListaDeIsentos as Lista,
  ResultadoDaPlanilha,
} from "../api/tipos";
import { Badge, Botao, Estado, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { supervisiona } from "../componentes/navegacao";
import { dataCurta, ultimoDia } from "../formato";

const SITUACAO: Record<
  LinhaDaLista["situacao"],
  { texto: string; cor: "verde" | "azul" | "cinza" | "amarelo" }
> = {
  VIGENTE: { texto: "vale", cor: "verde" },
  FUTURA: { texto: "ainda não vale", cor: "azul" },
  ENCERRADA: { texto: "encerrada", cor: "cinza" },
  REMOVIDA: { texto: "removida", cor: "amarelo" },
};

/** O modelo da planilha: as colunas que o servidor lê, com duas linhas de exemplo. */
export const MODELO_DA_PLANILHA = [
  "placa;tipologia;fundamento;desde;ate;concessao",
  "ABC1D23;Oficial;Convênio 7/2026 com a Prefeitura;01/10/2026;31/12/2026;",
  "XYZ9K87;Veículo operacional;Frota própria da concessionária;;;",
].join("\n");

function dia(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
  });
}

function vigencia(desde: string, ate: string | null | undefined): string {
  return `${dia(desde)} → ${ate ? ultimoDia(ate) : "sem fim"}`;
}

function mensagem(falha: unknown, padrao: string): string {
  return falha instanceof ErroDaApi ? falha.message : padrao;
}

export function ListaDeIsentos() {
  const { quem } = useSessao();
  const [busca, definirBusca] = useState("");
  const [situacao, definirSituacao] = useState<"vigentes" | "todas">(
    "vigentes",
  );
  const [lista, definirLista] = useState<Lista | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const consulta = new URLSearchParams({ situacao });
    if (busca.trim()) consulta.set("busca", busca.trim());
    try {
      definirLista(await api<Lista>(`/tollvision/isentos?${consulta}`));
      definirErro(null);
    } catch (falha) {
      definirErro(mensagem(falha, "Não foi possível ler a lista de isentos."));
    }
  }, [busca, situacao]);

  const podeManter = supervisiona(quem?.perfil);
  useEffect(() => {
    if (podeManter) void carregar();
  }, [carregar, podeManter]);

  if (!podeManter) {
    return (
      <Estado
        tipo="bloqueado"
        titulo="A lista de isentos é da supervisão."
        detalhe="Quem inclui e remove placas é o supervisor ou o administrador. Na conferência, a isenção de cada trânsito aparece com a fonte."
      />
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Lista de isentos
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            As placas que a concessionária isenta, com o fundamento e a vigência
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Badge cor="cinza">
            {lista?.fonte ?? "lista de isentos do cockpit — não é base oficial"}
          </Badge>
          <Link
            to="/senatran"
            className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel-2 px-2.5 text-[11.5px] font-semibold text-tinta-2 hover:bg-realce"
          >
            ‹ Voltar à SENATRAN
          </Link>
        </div>
      </div>

      <p className="max-w-[110ch] text-[11.5px] text-tinta-3">
        A isenção de uma passagem vale nesta ordem: <b>esta lista</b>, no
        instante da passagem; o <b>cadastro do veículo</b>; e, quando a placa
        não está em nenhum dos dois, a <b>caracterização</b> — o que está
        escrito no veículo (viatura, bombeiros, prefeitura, o operacional da
        concessionária). É sugestão: quem informa o desfecho continua sendo a
        supervisão.
      </p>

      <div className="grid min-h-0 grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_380px] xl:items-start">
        <Painel
          titulo="Placas na lista"
          rolagemPropria
          acoes={
            <span className="text-[11px] text-tinta-3">
              {lista
                ? `${lista.total} ${lista.total === 1 ? "linha" : "linhas"}`
                : ""}
            </span>
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-[11px] text-tinta-3">
              <Icone.Busca />
              <input
                value={busca}
                onChange={(e) => definirBusca(e.target.value.toUpperCase())}
                placeholder="Placa"
                aria-label="Buscar placa"
                maxLength={8}
                className="mono min-h-8 w-28 rounded-botao border border-linha bg-painel-2 px-2 text-[16px] text-tinta md:text-[11.5px]"
              />
            </label>
            <label className="inline-flex items-center gap-1.5 text-[11px] text-tinta-3">
              Mostrar
              <select
                value={situacao}
                aria-label="Quais linhas mostrar"
                onChange={(e) =>
                  definirSituacao(e.target.value as "vigentes" | "todas")
                }
                className="min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta"
              >
                <option value="vigentes">as que valem</option>
                <option value="todas">todas, com o histórico</option>
              </select>
            </label>
          </div>
          {erro ? (
            <Estado tipo="erro" titulo={erro} />
          ) : lista === null ? (
            <Estado tipo="carregando" titulo="Lendo a lista…" />
          ) : lista.linhas.length === 0 ? (
            <Estado
              tipo="vazio"
              titulo={
                busca
                  ? "Nenhuma placa com esse começo."
                  : "Nenhuma placa na lista."
              }
              detalhe="Inclua uma placa ao lado, ou importe a planilha da frota."
            />
          ) : (
            <div className="min-h-0 overflow-auto">
              <TabelaDaLista linhas={lista.linhas} aoMudar={carregar} />
            </div>
          )}
        </Painel>

        <div className="grid gap-3">
          {lista && <Inclusao lista={lista} aoIncluir={carregar} />}
          <Importacao aoGravar={carregar} />
          {lista && <VeiculosOperacionais lista={lista} />}
        </div>
      </div>
    </>
  );
}

function TabelaDaLista({
  linhas,
  aoMudar,
}: {
  linhas: LinhaDaLista[];
  aoMudar: () => Promise<void>;
}) {
  const [removendo, definirRemovendo] = useState<string | null>(null);
  return (
    <table className="w-full min-w-[760px] border-collapse text-[11.5px]">
      <thead className="sticky top-0 bg-painel-2 text-left text-rotulo tracking-[0.06em] text-tinta-3 uppercase">
        <tr>
          <th className="px-2 py-1.5">Placa</th>
          <th className="px-2 py-1.5">Tipologia</th>
          <th className="px-2 py-1.5">Concessão</th>
          <th className="px-2 py-1.5">Vigência</th>
          <th className="px-2 py-1.5">Fundamento</th>
          <th className="px-2 py-1.5">Situação</th>
          <th className="px-2 py-1.5" />
        </tr>
      </thead>
      <tbody>
        {linhas.map((l) => (
          <LinhaDaTabela
            key={l.id}
            linha={l}
            removendo={removendo === l.id}
            aoPedirRemocao={() => definirRemovendo(l.id)}
            aoCancelar={() => definirRemovendo(null)}
            aoRemover={async () => {
              definirRemovendo(null);
              await aoMudar();
            }}
          />
        ))}
      </tbody>
    </table>
  );
}

function LinhaDaTabela({
  linha: l,
  removendo,
  aoPedirRemocao,
  aoCancelar,
  aoRemover,
}: {
  linha: LinhaDaLista;
  removendo: boolean;
  aoPedirRemocao: () => void;
  aoCancelar: () => void;
  aoRemover: () => Promise<void>;
}) {
  const [motivo, definirMotivo] = useState("");
  const [recusa, definirRecusa] = useState<string | null>(null);
  const situacao = SITUACAO[l.situacao];
  const removivel = l.situacao !== "REMOVIDA" && l.situacao !== "ENCERRADA";

  async function remover() {
    try {
      await api(`/tollvision/isentos/${l.id}/remocao`, {
        method: "POST",
        corpo: { motivo },
      });
      definirMotivo("");
      definirRecusa(null);
      await aoRemover();
    } catch (falha) {
      definirRecusa(mensagem(falha, "Não foi possível remover."));
    }
  }

  return (
    <>
      <tr className="border-b border-linha-suave align-top">
        <td className="mono px-2 py-1.5 font-bold text-tinta">{l.placa}</td>
        <td className="px-2 py-1.5 text-tinta-2">{l.enquadramento}</td>
        <td className="px-2 py-1.5 text-tinta-2">{l.concessao ?? "Todas"}</td>
        <td className="px-2 py-1.5 whitespace-nowrap text-tinta-2">
          {vigencia(l.vigente_desde, l.vigente_ate)}
        </td>
        <td className="px-2 py-1.5 text-tinta-2">
          {l.fundamento}
          <span className="block text-[10.5px] text-tinta-3">
            {l.origem === "PLANILHA" ? "planilha" : "tela"} ·{" "}
            {l.incluido_por ?? "—"} · {dataCurta(l.incluido_em)}
          </span>
          {l.removido_em && (
            <span className="block text-[10.5px] text-amarelo">
              removida por {l.removido_por ?? "—"} em {dataCurta(l.removido_em)}
              : {l.motivo_da_remocao}
            </span>
          )}
        </td>
        <td className="px-2 py-1.5">
          <Badge cor={situacao.cor}>{situacao.texto}</Badge>
        </td>
        <td className="px-2 py-1.5 text-right">
          {removivel && !removendo && (
            <Botao onClick={aoPedirRemocao}>Remover</Botao>
          )}
        </td>
      </tr>
      {removendo && (
        <tr className="border-b border-linha-suave bg-painel-2">
          <td colSpan={7} className="px-2 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-tinta-3">
                Remover {l.placa} encerra a vigência agora; o que passou
                continua isento.
              </span>
              <input
                value={motivo}
                onChange={(e) => definirMotivo(e.target.value)}
                placeholder="Motivo da remoção"
                aria-label={`Motivo da remoção de ${l.placa}`}
                maxLength={300}
                className="min-h-8 min-w-60 flex-1 rounded-botao border border-linha bg-painel px-2 text-[16px] text-tinta md:text-[11.5px]"
              />
              <Botao
                papel="perigo"
                disabled={motivo.trim().length < 3}
                onClick={() => void remover()}
              >
                Confirmar remoção
              </Botao>
              <Botao onClick={aoCancelar}>Cancelar</Botao>
            </div>
            {recusa && (
              <p role="alert" className="pt-1 text-[11px] text-vermelho">
                {recusa}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

const CAMPO =
  "min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[16px] text-tinta md:text-[11.5px]";

function Inclusao({
  lista,
  aoIncluir,
}: {
  lista: Lista;
  aoIncluir: () => Promise<void>;
}) {
  const [placa, definirPlaca] = useState("");
  const [tipologia, definirTipologia] = useState(
    lista.tipologias[0]?.valor ?? "oficial",
  );
  const [concessao, definirConcessao] = useState(
    lista.pode_incluir_em_todas ? "" : (lista.concessoes[0]?.id ?? ""),
  );
  const [fundamento, definirFundamento] = useState("");
  const [desde, definirDesde] = useState("");
  const [ate, definirAte] = useState("");
  const [recusa, definirRecusa] = useState<string | null>(null);
  const [incluida, definirIncluida] = useState<string | null>(null);
  const [gravando, definirGravando] = useState(false);

  async function incluir(e: FormEvent) {
    e.preventDefault();
    definirGravando(true);
    definirRecusa(null);
    try {
      const nova = await api<LinhaDaLista>("/tollvision/isentos", {
        method: "POST",
        corpo: {
          placa,
          tipologia,
          concessao_id: concessao || null,
          fundamento,
          vigente_desde: desde || null,
          vigente_ate: ate || null,
        },
      });
      definirIncluida(`${nova.placa} incluída como ${nova.enquadramento}.`);
      definirPlaca("");
      definirFundamento("");
      definirAte("");
      await aoIncluir();
    } catch (falha) {
      definirIncluida(null);
      definirRecusa(mensagem(falha, "Não foi possível incluir."));
    } finally {
      definirGravando(false);
    }
  }

  return (
    <Painel titulo="Incluir placa">
      <form
        aria-label="Incluir placa na lista de isentos"
        className="grid gap-2"
        onSubmit={(e) => void incluir(e)}
      >
        <label className="grid gap-1 text-[11px] text-tinta-3">
          Placa
          <input
            value={placa}
            onChange={(e) => definirPlaca(e.target.value.toUpperCase())}
            maxLength={8}
            required
            className={`mono ${CAMPO}`}
          />
        </label>
        <label className="grid gap-1 text-[11px] text-tinta-3">
          Tipologia
          <select
            value={tipologia}
            onChange={(e) => definirTipologia(e.target.value)}
            className={CAMPO}
          >
            {lista.tipologias.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.rotulo}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-[11px] text-tinta-3">
          Concessão
          <select
            value={concessao}
            onChange={(e) => definirConcessao(e.target.value)}
            className={CAMPO}
          >
            {lista.pode_incluir_em_todas && (
              <option value="">Todas as concessões</option>
            )}
            {lista.concessoes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-[11px] text-tinta-3">
          Fundamento (ofício, credenciamento, convênio)
          <input
            value={fundamento}
            onChange={(e) => definirFundamento(e.target.value)}
            maxLength={300}
            required
            className={CAMPO}
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Vale desde (vazio: hoje)
            <input
              type="date"
              value={desde}
              onChange={(e) => definirDesde(e.target.value)}
              className={CAMPO}
            />
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Vale até (vazio: sem fim)
            <input
              type="date"
              value={ate}
              onChange={(e) => definirAte(e.target.value)}
              className={CAMPO}
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Botao type="submit" papel="primaria" disabled={gravando}>
            {gravando ? "Incluindo…" : "Incluir na lista"}
          </Botao>
          {incluida && (
            <span role="status" className="text-[11px] text-verde">
              {incluida}
            </span>
          )}
        </div>
        {recusa && (
          <p
            role="alert"
            className="rounded-botao border border-vermelho-borda bg-vermelho-fundo px-2.5 py-2 text-[11.5px] text-vermelho"
          >
            {recusa}
          </p>
        )}
      </form>
    </Painel>
  );
}

/** O texto do arquivo. `File.text()` é o caminho; o `FileReader` fica para quem não o tem. */
function lerTexto(arquivo: File): Promise<string> {
  if (typeof arquivo.text === "function") return arquivo.text();
  return new Promise((resolver, recusar) => {
    const leitor = new FileReader();
    leitor.onload = () => resolver(String(leitor.result ?? ""));
    leitor.onerror = () => recusar(leitor.error ?? new Error("leitura"));
    leitor.readAsText(arquivo);
  });
}

function baixarModelo() {
  // Sem `URL.createObjectURL` (o teste, um navegador muito antigo) não há o que baixar.
  if (typeof URL.createObjectURL !== "function") return;
  const url = URL.createObjectURL(
    new Blob([`\uFEFF${MODELO_DA_PLANILHA}\n`], {
      type: "text/csv;charset=utf-8",
    }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = "modelo-lista-de-isentos.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function Importacao({ aoGravar }: { aoGravar: () => Promise<void> }) {
  const [arquivo, definirArquivo] = useState<{
    nome: string;
    conteudo: string;
  } | null>(null);
  const [resultado, definirResultado] = useState<ResultadoDaPlanilha | null>(
    null,
  );
  const [recusa, definirRecusa] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  async function enviar(conteudo: string, nome: string, aplicar: boolean) {
    definirEnviando(true);
    definirRecusa(null);
    try {
      const r = await api<ResultadoDaPlanilha>("/tollvision/isentos/planilha", {
        method: "POST",
        corpo: { conteudo, nome_do_arquivo: nome, aplicar },
      });
      definirResultado(r);
      if (aplicar) await aoGravar();
    } catch (falha) {
      definirResultado(null);
      definirRecusa(mensagem(falha, "Não foi possível ler a planilha."));
    } finally {
      definirEnviando(false);
    }
  }

  async function escolher(lista: FileList | null) {
    const escolhido = lista?.[0];
    if (!escolhido) return;
    const conteudo = await lerTexto(escolhido);
    definirArquivo({ nome: escolhido.name, conteudo });
    await enviar(conteudo, escolhido.name, false);
  }

  return (
    <Painel
      titulo="Importar planilha"
      acoes={
        <Botao
          onClick={baixarModelo}
          title="As colunas que a importação lê, com dois exemplos."
        >
          <Icone.Baixar />
          Modelo
        </Botao>
      }
    >
      <p className="text-[11px] text-tinta-3">
        CSV com as colunas <span className="mono">placa</span>,{" "}
        <span className="mono">tipologia</span> e{" "}
        <span className="mono">fundamento</span>, e opcionais{" "}
        <span className="mono">desde</span>, <span className="mono">ate</span>{" "}
        (dd/mm/aaaa) e <span className="mono">concessao</span> (vazio: todas).
        Primeiro a prévia; nada é gravado até você confirmar.
      </p>
      <input
        type="file"
        accept=".csv,text/csv"
        aria-label="Planilha de isentos"
        onChange={(e) => void escolher(e.target.files)}
        className="text-[11.5px] text-tinta-2"
      />
      {recusa && (
        <p role="alert" className="text-[11.5px] text-vermelho">
          {recusa}
        </p>
      )}
      {resultado && (
        <section aria-label="Prévia da planilha" className="grid gap-1.5">
          <div className="flex flex-wrap gap-1.5">
            <Badge cor="azul">{`${resultado.linhas} linhas`}</Badge>
            <Badge cor="verde">
              {resultado.aplicada
                ? `${resultado.gravadas} gravadas`
                : `${resultado.validas} entram`}
            </Badge>
            {resultado.ja_na_lista > 0 && (
              <Badge cor="cinza">{`${resultado.ja_na_lista} já na lista`}</Badge>
            )}
            {resultado.problemas.length > 0 && (
              <Badge cor="vermelho">
                {`${resultado.problemas.length} com problema`}
              </Badge>
            )}
          </div>
          {resultado.problemas.length > 0 && (
            <ul className="grid max-h-40 gap-0.5 overflow-auto text-[11px] text-tinta-2">
              {resultado.problemas.slice(0, 50).map((p) => (
                <li key={`${p.linha}-${p.placa ?? ""}`}>
                  <span className="mono text-tinta-3">linha {p.linha}</span>
                  {p.placa ? ` · ${p.placa}` : ""}: {p.motivo}
                </li>
              ))}
            </ul>
          )}
          {!resultado.aplicada && resultado.validas > 0 && arquivo && (
            <Botao
              papel="aprovar"
              disabled={enviando}
              onClick={() => void enviar(arquivo.conteudo, arquivo.nome, true)}
            >
              {enviando
                ? "Gravando…"
                : `Gravar ${resultado.validas} ${resultado.validas === 1 ? "placa" : "placas"}`}
            </Botao>
          )}
        </section>
      )}
    </Painel>
  );
}

function VeiculosOperacionais({ lista }: { lista: Lista }) {
  return (
    <Painel titulo="Veículos operacionais">
      <p className="text-[11px] text-tinta-3">
        Fora da lista e do cadastro, o veículo da concessionária é sugerido
        isento pelo que está escrito nele. A IA procura estas frases:
      </p>
      <div className="flex flex-wrap gap-1">
        {lista.frases_operacionais.map((f) => (
          <Badge key={f} cor="cinza">
            {f}
          </Badge>
        ))}
      </div>
      <p className="text-[11px] text-tinta-3">
        E as palavras da concessionária:{" "}
        {lista.palavras_operacionais.length ? (
          <b className="text-tinta-2">
            {lista.palavras_operacionais.join(", ")}
          </b>
        ) : (
          <span>nenhuma ainda</span>
        )}
        . Quem as define é o administrador, em Administração → Configuração e
        saúde.
      </p>
    </Painel>
  );
}
